import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Envelope, PaperPlaneTilt, CheckCircle, XCircle, MinusCircle, ArrowClockwise } from "@phosphor-icons/react";

const CAT_LABEL = {
  welcome: "Welcome", invite: "Team Invite", password_reset: "Password Reset",
  trial_reminder: "Trial Reminder", trial_ended: "Trial Ended", payment_receipt: "Payment Receipt", lead_alert: "Lead Alert", portfolio_snapshot: "Portfolio Snapshot", weekly_digest: "Weekly Digest", general: "General",
};

const StatusBadge = ({ status }) => {
  const map = {
    sent: ["bg-emerald-100 text-emerald-800", CheckCircle],
    failed: ["bg-red-100 text-red-800", XCircle],
    skipped: ["bg-secondary text-muted-foreground", MinusCircle],
  };
  const [cls, Icon] = map[status] || map.skipped;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm capitalize ${cls}`}><Icon size={13} weight="bold" /> {status}</span>;
};

const DELIVERY_STYLE = {
  delivered: "bg-emerald-100 text-emerald-800",
  opened: "bg-blue-100 text-blue-800",
  clicked: "bg-indigo-100 text-indigo-800",
  bounced: "bg-red-100 text-red-800",
  complained: "bg-orange-100 text-orange-800",
  delayed: "bg-amber-100 text-amber-800",
  sent: "bg-secondary text-muted-foreground",
};

const DeliveryBadge = ({ status, reason }) => {
  if (!status) return null;
  return <span title={reason || ""} className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-sm capitalize ${DELIVERY_STYLE[status] || "bg-secondary text-muted-foreground"}`} data-testid="delivery-badge">{status}</span>;
};

export default function EmailLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(null);
  const [delivBusy, setDelivBusy] = useState(false);
  const [deliv, setDeliv] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-log", { params: { limit: 200, category: filter || undefined, status: failedOnly ? "failed" : undefined } })
      .then((r) => setLogs(r.data.logs || []))
      .catch(() => toast.error("Could not load email log"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter, failedOnly]);

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Enter a valid email address"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/email-test", { to: testTo });
      toast.success(`Sent ${r.data.count} sample emails to ${r.data.sent_to}`);
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test emails");
    } finally { setSending(false); }
  };

  const sendDeliverability = async () => {
    if (!testTo.includes("@")) { toast.error("Enter a valid email address"); return; }
    setDelivBusy(true);
    setDeliv(null);
    try {
      const r = await api.post("/admin/deliverability-test", { to: testTo });
      setDeliv(r.data);
      if (r.data.sent) toast.success(`Test email sent to ${r.data.to}`);
      else toast.error("Send failed — see details below");
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email");
    } finally { setDelivBusy(false); }
  };

  const resend = async (log) => {
    if (!log.log_id) { toast.error("This log entry can't be resent"); return; }
    setResending(log.log_id);
    try {
      const r = await api.post("/admin/resend-email", { log_id: log.log_id });
      toast.success(`Resent to ${r.data.to || log.to}`);
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend email");
    } finally { setResending(null); }
  };

  const cats = ["", "welcome", "invite", "password_reset", "trial_reminder", "trial_ended", "payment_receipt"];

  return (
    <div data-testid="email-log-page">
      <PageHeader title="Email Log" subtitle="Every transactional email sent by RecoverOS, with delivery status">
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="rounded-sm w-52 h-9" data-testid="email-test-to" />
          <Button onClick={sendDeliverability} disabled={delivBusy} variant="outline" className="rounded-sm gap-1.5" data-testid="email-deliverability-send">
            <PaperPlaneTilt size={15} weight="bold" /> {delivBusy ? "Sending…" : "Deliverability test"}
          </Button>
          <Button onClick={sendTest} disabled={sending} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="email-test-send">
            <PaperPlaneTilt size={15} weight="bold" /> {sending ? "Sending…" : "Send sample of each"}
          </Button>
        </div>
      </PageHeader>

      {deliv && (
        <div className={`border p-4 mb-4 rounded-sm ${deliv.sent ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="deliverability-result">
          <div className="flex items-center gap-2 font-semibold">
            {deliv.sent ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <XCircle size={18} weight="fill" className="text-red-600" />}
            {deliv.sent ? `Test email sent to ${deliv.to}` : "Test email failed to send"}
          </div>
          <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
            <div>Provider: <span className="font-mono uppercase">{deliv.provider}</span> · From: <span className="font-mono">{deliv.from}</span> · Status: <span className="font-semibold">{deliv.status}</span>{deliv.message_id ? <> · ID: <span className="font-mono">{deliv.message_id}</span></> : null}</div>
            {deliv.error ? <div className="text-red-600">Error: {deliv.error}</div> : null}
            {deliv.sent ? <div className="text-foreground mt-1">Now open it in Gmail → <strong>⋮ → Show original</strong> and confirm <strong>SPF / DKIM / DMARC = PASS</strong>. If it landed in Spam, complete domain (DKIM) verification at your provider.</div> : null}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {cats.map((c) => (
          <button key={c || "all"} onClick={() => setFilter(c)}
            className={`px-3 py-1 text-xs font-semibold rounded-sm border transition-colors ${filter === c ? "bg-[#0B3D91] text-white border-[#0B3D91]" : "bg-card border-border hover:border-[#0B3D91]/40"}`}
            data-testid={`email-filter-${c || "all"}`}>
            {c ? CAT_LABEL[c] : "All"}
          </button>
        ))}
        <button onClick={() => setFailedOnly((v) => !v)}
          className={`px-3 py-1 text-xs font-semibold rounded-sm border transition-colors inline-flex items-center gap-1 ${failedOnly ? "bg-red-600 text-white border-red-600" : "bg-card border-border hover:border-red-400"}`}
          data-testid="email-filter-failed-only">
          <XCircle size={13} weight="bold" /> Failed only
        </button>
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto gap-1 h-8" data-testid="email-log-refresh"><ArrowClockwise size={14} /> Refresh</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Sent At</th>
              <th className="text-left p-3">Recipient</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Subject</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Delivery</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground" data-testid="email-log-empty"><Envelope size={28} className="mx-auto mb-2 opacity-40" />No emails sent yet.</td></tr>
            ) : logs.map((l, i) => (
              <tr key={i} className="border-b border-border last:border-0" data-testid={`email-log-row-${i}`}>
                <td className="p-3 whitespace-nowrap text-muted-foreground">{fmtDateTime(l.created_at)}</td>
                <td className="p-3 whitespace-nowrap">{l.to}</td>
                <td className="p-3 whitespace-nowrap">{CAT_LABEL[l.category] || l.category}{l.provider ? <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-secondary text-muted-foreground uppercase">{l.provider}</span> : null}</td>
                <td className="p-3">{l.subject}{l.status === "failed" && l.error ? <div className="text-xs text-red-600 mt-0.5">{l.error}{l.retry_count ? ` (after ${l.retry_count} retries)` : ""}</div> : null}{l.status === "sent" && l.message_id ? <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">ID: {l.message_id}</div> : null}</td>
                <td className="p-3 text-center"><StatusBadge status={l.status} /></td>
                <td className="p-3 text-center"><DeliveryBadge status={l.delivery_status} reason={l.bounce_reason} /></td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" disabled={!l.log_id || !l.html || resending === l.log_id}
                    title={!l.log_id || !l.html ? "Legacy log entry — email body not stored, cannot resend" : "Resend this email"}
                    onClick={() => resend(l)} className="rounded-sm gap-1 h-7 text-xs" data-testid={`email-resend-${i}`}>
                    <ArrowClockwise size={13} weight="bold" /> {resending === l.log_id ? "Sending…" : "Resend"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
